import { AssistantEntry } from "src/app/assistant/assistant-entry";
import { authOrigins } from "./shared/auth/auth-origins";
import { NativeHandoffService } from "./shared/auth/native-handoff.service";
import { FfModalService } from "src/app/shared/core/ff-modal.service";
import {
  Component,
  NgZone,
  OnInit,
  ViewChild,
  CUSTOM_ELEMENTS_SCHEMA,
  OnDestroy,
  ChangeDetectionStrategy,
  inject,
} from "@angular/core";
import { NavigationExtras, Router, RouterLink } from "@angular/router";
import { Capacitor, PluginListenerHandle } from "@capacitor/core";
import { SplashScreen } from "@capacitor/splash-screen";
import { AlertController, ModalController, Platform, ToastController, IonSelect } from "@ionic/angular";
import { HoldingSelectModalComponent } from "./components/manage/holding/holding-select-modal/holding-select-modal.component";
import { take } from "rxjs/operators";

import { Browser } from "@capacitor/browser";
import { ConnectionStatus, Network } from "@capacitor/network";
import { Subscription } from "rxjs";
import { environment } from "../environments/environment";
import { AccountService } from "./account/account.service";
import { CoreService } from "./shared/core/core.service";
import { AuthenticationService } from "./shared/auth/authentication-service";
import { PosthogService } from "./shared/core/posthog.service";
import { ConsentService } from "./shared/consent/consent.service";
import { AcquisitionService } from "./shared/acquisition/acquisition.service";
import { App, URLOpenListenerEvent } from "@capacitor/app";
import { addIcons } from "ionicons";
import {
  home,
  homeOutline,
  mapOutline,
  bandageOutline,
  ellipsisHorizontalOutline,
  bandage,
  map,
  ellipsisHorizontal,
  personOutline,
  personCircleOutline,
  settingsOutline,
  chevronExpandOutline,
} from "ionicons/icons";
import {
  IonContent,
  IonFooter,
  IonHeader,
  IonIcon,
  IonItem,
  IonLabel,
  IonList,
  IonMenu,
  IonMenuToggle,
  IonRouterOutlet,
  IonSelect as IonSelectStandalone,
  IonSelectOption,
  IonSplitPane,
} from "@ionic/angular";
import { UserHolding } from "./account/account.model";

import { register } from "swiper/element/bundle";
import { IonRouterLink } from "@ionic/angular";
import { LisBetaDirective } from "src/app/shared/beta/lis-beta.directive";
register();

interface MenuEntry {
  /** The `isRouteActive` key. */
  key: string;
  label: string;
  path: string;
  icon?: string;
  activeIcon?: string;
  iconSrc?: string;
  activeIconSrc?: string;
}

import { HoldingSwitchService } from "src/app/shared/core/holding-switch.service";
@Component({
  selector: "app-root",
  templateUrl: "app.component.html",
  styleUrls: ["app.component.scss"],
  standalone: true,
  imports: [
    AssistantEntry,
    LisBetaDirective,
    RouterLink,
    IonRouterLink,
    IonContent,
    IonFooter,
    IonHeader,
    IonIcon,
    IonItem,
    IonLabel,
    IonList,
    IonMenu,
    IonMenuToggle,
    IonRouterOutlet,
    IonSelectStandalone,
    IonSelectOption,
    IonSplitPane,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class AppComponent implements OnInit, OnDestroy {
  private readonly nativeHandoff = inject(NativeHandoffService);
  private readonly ffModal = inject(FfModalService);
  private readonly holdingSwitch = inject(HoldingSwitchService);
  private readonly consent = inject(ConsentService);
  private readonly acquisition = inject(AcquisitionService);
  private _loggedInObserver: Subscription = new Subscription();
  private _routerSubscription: Subscription;
  private _holdingsListener: Subscription;
  private _holdingListener: Subscription;

  isDesktop: boolean;
  appVersion: string;
  isMobile = false;
  isIOS = false;
  flockFinderOnline = environment.WEBSITE_ADDRESS;
  networkStatus: ConnectionStatus;
  networkListener: Promise<PluginListenerHandle>;

  public flockImg = "/assets/icon/white/sheep-white.svg";
  public flockImgActive = "/assets/icon/success/sheep_success.svg";

  // The desktop menu is data-driven so every row shares one template and one set of styles.
  // Outline icons at rest, filled for the active route (design principles, Icons).
  readonly menuPages: MenuEntry[] = [
    { key: "home", label: "Dashboard", path: "/manage/home", icon: "home-outline", activeIcon: "home" },
    { key: "fields", label: "Fields", path: "/manage/fields", icon: "location-outline", activeIcon: "location" },
    {
      key: "flock",
      label: "Livestock",
      path: "/manage/flock",
      iconSrc: this.flockImg,
      activeIconSrc: this.flockImgActive,
    },
    { key: "medical", label: "Treatments", path: "/manage/medical", icon: "bandage-outline", activeIcon: "bandage" },
  ];
  readonly menuMore: MenuEntry[] = [
    { key: "medical-cabinet", label: "Medicine cabinet", path: "/manage/medical/medical-cabinet" },
    { key: "suppliers", label: "Suppliers", path: "/manage/medical/suppliers" },
    { key: "movement-history", label: "Movements", path: "/manage/movement-history" },
    { key: "sessions", label: "Sessions", path: "/manage/sessions" },
    { key: "breeding", label: "Breeding", path: "/manage/breeding" },
    { key: "labels", label: "Labels", path: "/manage/labels" },
    { key: "data", label: "Data", path: "/manage/data" },
  ];

  // Holding selection properties
  selectedHolding: UserHolding;
  userHoldings: UserHolding[] = [];
  customPopoverOptions: any = {
    header: "Select Holding",
    message: "To edit holdings please go to your account settings",
  };
  @ViewChild("holdingSelect") holdingSelect: IonSelect;

  // Menu sections
  isMoreExpanded = false;
  isAccountExpanded = false;

  constructor(
    private platform: Platform,
    private authService: AuthenticationService,
    private router: Router,
    private core: CoreService,
    private accountService: AccountService,
    private alertController: AlertController,
    private toastController: ToastController,
    private modalController: ModalController,
    private posthogService: PosthogService,
    private zone: NgZone,
  ) {
    // Register icons
    addIcons({
      home,
      homeOutline,
      mapOutline,
      bandageOutline,
      ellipsisHorizontalOutline,
      bandage,
      map,
      ellipsisHorizontal,
      personOutline,
      personCircleOutline,
      settingsOutline,
      chevronExpandOutline,
    });

    this.initializeApp();
    this.core.isDesktopView().subscribe(isDesktop => {
      this.isDesktop = isDesktop;
    });
    this.appVersion = environment.version;

    if (this.platform.is("mobile")) {
      this.isMobile = true;
    }
    if (this.platform.is("ios") && !this.platform.is("mobileweb")) {
      this.isIOS = true;
    }
  }

  initializeApp() {
    this.platform.ready().then(async () => {
      if (Capacitor.isPluginAvailable("SplashScreen")) {
        SplashScreen.hide();
      }
      this.acquisition.start();
      await this.consent.initialise();
      this.posthogService.capture("app_launched", {
        version: environment.version,
        platform: Capacitor.getPlatform(),
      });
    });
    const openAppUrl = (event: URLOpenListenerEvent) => {
      try {
        const url = new URL(event.url);
        if (url.origin !== authOrigins().app || url.username || url.password) return;
        if (url.pathname.startsWith("/auth/native/")) {
          this.zone.run(() => {
            void this.nativeHandoff.handle(url);
          });
          return;
        }
        if (url.pathname === "/auth/website") {
          (window as Window & { __flockAppHandoff?: string }).__flockAppHandoff = url.hash.slice(1);
          this.zone.run(() => {
            void this.router.navigateByUrl("/auth/website", { onSameUrlNavigation: "reload" });
          });
          return;
        }
        this.zone.run(() => {
          void this.router.navigateByUrl(url.pathname + url.search + url.hash);
        });
      } catch {
        /* Malformed native links cannot select an app route. */
      }
    };
    App.addListener("appUrlOpen", openAppUrl);
    if (Capacitor.isNativePlatform()) {
      void App.getLaunchUrl().then(event => {
        if (event) openAppUrl(event);
      });
    }
  }

  goToHome() {
    this.navigateToTab("home");
  }

  // Helper method for tab navigation
  navigateToTab(tabPath: string) {
    // Use the NavController to navigate to tabs
    this.router.navigate(["/manage/" + tabPath]);
  }

  navigateTo(url: string) {
    this.router.navigateByUrl(url);
  }

  onLogout() {
    this.authService.SignOut();
  }

  goToFlockFinderWeb() {
    this.openLink(this.flockFinderOnline);
  }

  async goToHelp() {
    await this.core.goToHelp();
  }

  toggleMoreSection() {
    this.isMoreExpanded = !this.isMoreExpanded;

    // Close account section when opening more section
    if (this.isMoreExpanded) {
      this.isAccountExpanded = false;
    }
  }

  toggleAccountSection() {
    this.isAccountExpanded = !this.isAccountExpanded;

    // Close more section when opening account section
    if (this.isAccountExpanded) {
      this.isMoreExpanded = false;
    }
  }

  /** A new movement is routed under Livestock, but the keeper is in Movements (FLO-679 H5). */
  private isMovementFlow(): boolean {
    const url = this.router.url;
    return url.includes("/manage/flock/moving-off") || url.includes("/manage/flock/movements");
  }

  isRouteActive(route: string): boolean {
    if (route === "home") {
      return this.router.url.includes("/manage/home");
    } else if (route === "fields") {
      return this.router.url.includes("/manage/fields");
    } else if (route === "flock") {
      return this.router.url.includes("/manage/flock") && !this.isMovementFlow();
    } else if (route === "medical") {
      return (
        this.router.url.includes("/manage/medical") &&
        !this.router.url.includes("/manage/medical/medical-cabinet") &&
        !this.router.url.includes("/manage/medical/suppliers")
      );
    } else if (route === "medical-cabinet") {
      return this.router.url.includes("/manage/medical/medical-cabinet");
    } else if (route === "suppliers") {
      return this.router.url.includes("/manage/medical/suppliers");
    } else if (route === "movement-history") {
      return this.router.url.includes("/manage/movement-history") || this.isMovementFlow();
    } else if (route === "sessions") {
      return this.router.url.includes("/manage/sessions");
    } else if (route === "breeding") {
      return this.router.url.includes("/manage/breeding");
    } else if (route === "labels") {
      return this.router.url.includes("/manage/labels");
    } else if (route === "data") {
      return this.router.url.includes("/manage/data");
    } else if (route === "connections") {
      return this.router.url.includes("/account/connections");
    } else if (route === "account") {
      // Connections is its own child of /account, so Account Settings must not claim it too.
      return this.router.url.includes("/account") && !this.router.url.includes("/account/connections");
    } else if (route === "holdings") {
      return this.router.url.includes("/manage/holdings");
    }
    return false;
  }

  isMoreActive(): boolean {
    return this.menuMore.some(entry => this.isRouteActive(entry.key));
  }

  isAccountActive(): boolean {
    return this.isRouteActive("holdings") || this.isRouteActive("account") || this.isRouteActive("connections");
  }

  checkAndExpandSections() {
    if (this.isMoreActive()) {
      this.isMoreExpanded = true;
    }
    if (this.isAccountActive()) {
      this.isAccountExpanded = true;
    }
  }

  // Update menu item navigation methods to use proper tab navigation
  navManage(url: string) {
    this.accountService.selectedUserHoldingGlobal.pipe(take(1)).subscribe(selectedHolding => {
      if (selectedHolding !== null) {
        // For tab paths, use navigateToTab
        const isTabPath = url.split("/").length === 3 && url.startsWith("/manage/");

        if (isTabPath) {
          const tabName = url.split("/")[2];
          this.navigateToTab(tabName);
        } else {
          // For deeper paths, use regular navigation
          const navigationExtras: NavigationExtras = {
            state: {
              sourcePage: this.router.url,
              selectedHolding: selectedHolding,
            },
          };
          this.router.navigate([url], navigationExtras);
        }
      } else {
        this.noHoldingSelected();
      }
    });
  }

  noHoldingSelected() {
    this.accountService.userHoldings.pipe(take(1)).subscribe(userHoldings => {
      if (userHoldings.length === 0) {
        this.goToHoldingsAlert();
      } else {
        this.selectHoldingToast();
      }
    });
  }

  async goToHoldingsAlert() {
    const alert = await this.alertController.create({
      message: "To continue you must add a holding to your account",
      buttons: [
        {
          text: "Cancel",
          role: "cancel",
          cssClass: "secondary",
        },
        {
          text: "Okay",
          handler: () => {
            this.router.navigateByUrl("account");
          },
        },
      ],
    });
    await alert.present();
  }

  async selectHoldingToast() {
    const toast = await this.toastController.create({
      message: "Select holding from dropdown.",
      duration: 2000,
      position: "top",
    });
    toast.present();
  }

  async openLink(url: string) {
    await Browser.open({ url: url, presentationStyle: "popover" });
  }

  storeHoldingSelection(event: CustomEvent) {
    const selectedHoldingId = event.detail.value;
    this.selectedHolding = this.userHoldings.find(holding => holding.id === selectedHoldingId);
    if (this.selectedHolding) {
      this.accountService.updateSelectedHolding(this.selectedHolding);
    }
  }

  openHoldingSelect() {
    this.openHoldingSelectModal();
  }

  /** A designed dialog for the control that scopes every screen, on both shapes, not a stock alert. */
  private holdingModalOpen = false;

  private async openHoldingSelectModal() {
    if (this.holdingModalOpen) {
      return;
    }
    this.holdingModalOpen = true;
    try {
      const modal = await this.ffModal.create({
        component: HoldingSelectModalComponent,
        componentProps: { holdings: this.userHoldings, selectedId: this.selectedHolding?.id ?? null },
        cssClass: "ff-modal ff-modal-holding",
        backdropDismiss: true,
      });
      await modal.present();
      const { data, role } = await modal.onWillDismiss<UserHolding>();
      if (role === "select" && data && data.id !== this.selectedHolding?.id) {
        this.selectedHolding = data;
        this.holdingSwitch.switchTo(data);
      }
    } finally {
      // Without this a single failed present would leave the flag set and the switcher dead for
      // the rest of the session, with no way back to another holding but a reload.
      this.holdingModalOpen = false;
    }
  }

  ngOnInit() {
    // tracking network status changes
    this.networkListener = Network.addListener("networkStatusChange", status => {
      this.networkStatus = status;
      // Network status changes are now handled by the OfflineModeService and offline banner
    });

    // Check if sections should be expanded based on current route
    this.checkAndExpandSections();

    // Listen for route changes to auto-expand relevant sections
    this._routerSubscription = this.router.events.subscribe(() => {
      this.checkAndExpandSections();
    });

    // Initialize holdings data
    this._holdingsListener = this.accountService.userHoldings.subscribe(_userHoldings => {
      // restrict dropdown to only those with permissionGranted
      this.userHoldings = _userHoldings.filter(holding => {
        return holding.permissionGranted === true;
      });
    });

    this._holdingListener = this.accountService.selectedUserHoldingGlobal.subscribe(holding => {
      this.selectedHolding = holding;
    });

    // Get initial network status
    Network.getStatus().then(status => {
      this.networkStatus = status;
    });
  }

  ngOnDestroy() {
    this._loggedInObserver.unsubscribe();
    if (this._routerSubscription) {
      this._routerSubscription.unsubscribe();
    }
    if (this._holdingsListener) {
      this._holdingsListener.unsubscribe();
    }
    if (this._holdingListener) {
      this._holdingListener.unsubscribe();
    }
    Network.removeAllListeners();
  }
}
